/**
 * Smart wrapper composing all onboarding primitives.
 * The orchestrator (`SignupPage.tsx` in the host project) owns state via
 * `useOnboarding()` and passes it down. This wrapper is purely presentational
 * and accepts the same shape — making it drop-in for the demo and Storybook.
 */
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { OnboardingLayout } from "./OnboardingLayout";
import { OnboardingStepIndicator } from "./OnboardingStepIndicator";
import { AccountStep } from "./AccountStep";
import { BusinessStep } from "./BusinessStep";
import { BranchStep } from "./BranchStep";
import { OnboardingActionBar } from "./OnboardingActionBar";
import { OnboardingErrorBanner } from "./OnboardingErrorBanner";
import { OnboardingSuccessState } from "./OnboardingSuccessState";
import type {
  OnboardingApiError,
  OnboardingDraft,
  OnboardingLabels,
  StepError,
  SubVerticalOption,
  WizardStep,
} from "./types";

export interface OnboardingWizardProps {
  step: WizardStep;
  draft: OnboardingDraft;
  password: string;
  canAdvance: boolean;
  isProvisioning: boolean;
  isComplete?: boolean;
  stepErrors?: StepError[];
  provisioningError?: OnboardingApiError | null;
  subVerticalOptions: SubVerticalOption[];
  labels: OnboardingLabels;
  resumeBanner?: React.ReactNode;
  onSetStep: (step: WizardStep) => void;
  onUpdateAccount: (patch: Partial<OnboardingDraft["account"]>) => void;
  onUpdatePassword: (password: string) => void;
  onUpdateBusiness: (patch: Partial<OnboardingDraft["business"]>) => void;
  onUpdateBranch: (patch: Partial<OnboardingDraft["branch"]>) => void;
  onNext: () => void;
  onBack: () => void;
  onSubmit: () => void;
  onLogin?: () => void;
  onContactSupport?: () => void;
  onContinueToDashboard?: () => void;
  onDismissError?: () => void;
  className?: string;
}

export function OnboardingWizard(props: OnboardingWizardProps) {
  const {
    step,
    draft,
    password,
    canAdvance,
    isProvisioning,
    isComplete,
    stepErrors,
    provisioningError,
    subVerticalOptions,
    labels,
    resumeBanner,
    onSetStep,
    onUpdateAccount,
    onUpdatePassword,
    onUpdateBusiness,
    onUpdateBranch,
    onNext,
    onBack,
    onSubmit,
    onLogin,
    onContactSupport,
    onContinueToDashboard,
    onDismissError,
    className,
  } = props;

  const [reduceMotion] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );

  const selectedVertical = subVerticalOptions.find((o) => o.key === draft.business.subVerticalKey);

  if (isComplete) {
    return (
      <OnboardingLayout labels={labels} onLoginClick={onLogin}>
        <div className="rounded-biz-card border border-biz-border bg-biz-surface p-6 sm:p-10 shadow-biz-md">
          <OnboardingSuccessState
            labels={labels}
            email={draft.account.email}
            onContinue={onContinueToDashboard}
          />
        </div>
      </OnboardingLayout>
    );
  }

  return (
    <OnboardingLayout
      labels={labels}
      resumeBanner={resumeBanner}
      onLoginClick={onLogin}
      className={className}
    >
      <div className="flex flex-col">
        <OnboardingStepIndicator current={step} labels={labels} onStepClick={onSetStep} />

        <div
          className="mt-6 rounded-biz-card border border-biz-border bg-biz-surface p-5 shadow-biz-md sm:p-8"
          aria-live="polite"
        >
          <header className="mb-6">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-biz-primary">
              {`0${step}`} · {labels.steps[step].title}
            </p>
            <h2 className="mt-1 font-display text-2xl font-bold tracking-tight text-biz-foreground sm:text-[1.75rem]">
              {labels.steps[step].description}
            </h2>
          </header>

          {provisioningError && (
            <div className="mb-5">
              <OnboardingErrorBanner
                error={provisioningError}
                labels={labels}
                onDismiss={onDismissError}
                onLogin={onLogin}
                onContactSupport={onContactSupport}
              />
            </div>
          )}

          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={step}
              initial={reduceMotion ? false : { opacity: 0, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              exit={reduceMotion ? undefined : { opacity: 0, x: -12 }}
              transition={{ duration: reduceMotion ? 0 : 0.18, ease: [0.32, 0.72, 0, 1] }}
            >
              {step === 1 && (
                <AccountStep
                  value={draft.account}
                  password={password}
                  onChange={onUpdateAccount}
                  onPasswordChange={onUpdatePassword}
                  errors={stepErrors}
                  labels={labels}
                />
              )}
              {step === 2 && (
                <BusinessStep
                  value={draft.business}
                  onChange={onUpdateBusiness}
                  options={subVerticalOptions}
                  errors={stepErrors}
                  labels={labels}
                  locale={draft.account.locale}
                />
              )}
              {step === 3 && (
                <BranchStep
                  value={draft.branch}
                  onChange={onUpdateBranch}
                  errors={stepErrors}
                  labels={labels}
                  defaultOperatingHours={selectedVertical?.defaultOperatingHours}
                />
              )}
            </motion.div>
          </AnimatePresence>

          <OnboardingActionBar
            step={step}
            canAdvance={canAdvance}
            isProvisioning={isProvisioning}
            onBack={onBack}
            onNext={onNext}
            onSubmit={onSubmit}
            labels={labels.actions}
          />
        </div>

        <p className={cn("mt-6 text-center text-xs text-biz-foreground-muted")}>
          v1 · {new Date(draft.updatedAt).toLocaleString()}
        </p>
      </div>
    </OnboardingLayout>
  );
}
